//@flow
/* eslint-disable react/jsx-no-bind */
import * as React from 'react';
import PjpMetricCardContainer from './PjpMetricCardContainer';
import { Map } from 'immutable';

import ControlledSetInterval from 'common/utils/ControlledSetInterval';

import { ReportGrid } from 'common/components/common/analytics';
import type {
    PjpInsightsDashboard,
    PjpInsightsDashboardData
} from 'common/models/internal/PjpInsightsDashboard';
import { type TFunction } from 'react-i18next';

type LayoutType = {
    i: string,
    x: number,
    y: number,
    w: number,
    h: number,
    minW: number,
    minH: number
};

type Props = {
    allDashboards: Array<PjpInsightsDashboard>,
    dashboard: PjpInsightsDashboard,
    clientId: string,
    dashboard: {
        dashboardData: PjpInsightsDashboardData
    },
    planId: string,
    fetchMetric: (
        metricId: string,
        aggregate: string,
        groupBy: Array<string>,
        graphId: string,
        includeExistingBeat: boolean,
        isEditMetricActive: boolean
    ) => void,
    pollFetchMetric: (clientId: string, poller: ControlledSetInterval) => void,
    savedLayout: ?{ [breakpoint: string]: Array<LayoutType> },
    onLayoutChange: any => void,
    onEditChart: (dashboardId: string) => void,
    onDeleteChart: (dashboardId: string) => void,
    t: TFunction,
    graphsWithExistingBeat: Map<string, boolean>,
    toggleExistingBeat: (graphId: string) => void
};

type State = {
    currentBreakpoint: string,
    currentCols: ?number
};

const layoutOptions: { w: number, h: number, minW: number, minH: number } = {
    w: 1,
    h: 3,
    minW: 1,
    minH: 2
};

const PjpGraphsGrid = (props: Props) => {
    const [state, setState] = React.useState(
        ({
            currentBreakpoint: null,
            currentCols: null
        }: State)
    );

    const items = React.useMemo(() => {
        return (
            props.dashboard.dashboardData.graphs.map(graph => ({
                id: graph.id,
                value: graph
            })) || []
        );
    }, [props.dashboard.dashboardData.graphs]);

    const handleBreakpointChange = React.useCallback(
        (newBreakpoint, newCols) => {
            setState({
                ...state,
                currentBreakpoint: newBreakpoint,
                currentCols: newCols
            });
        },
        [state]
    );

    const {
        clientId,
        pollFetchMetric,
        fetchMetric,
        planId,
        onEditChart,
        dashboard,
        onDeleteChart,
        toggleExistingBeat,
        graphsWithExistingBeat,
        t
    } = props;
    const renderItem = React.useCallback(
        params => {
            const existingBeatEnabled = graphsWithExistingBeat.has(
                params.item.id
            )
                ? true
                : false;
            return (
                <>
                    <PjpMetricCardContainer
                        t={t}
                        cardType={!existingBeatEnabled ? 'Current Beat' : null}
                        onDeleteChart={onDeleteChart}
                        onEditChart={onEditChart}
                        existingBeatEnabled={existingBeatEnabled}
                        graphsWithExistingBeat={graphsWithExistingBeat}
                        toggleExistingBeat={toggleExistingBeat}
                        planId={planId}
                        className={params.itemClassName}
                        cardHeight={params.itemHeight}
                        minCardHeight={params.itemMinHeight}
                        gridWidth={params.width}
                        currentCols={state.currentCols}
                        graph={params.item}
                        clientId={clientId}
                        pollFetchMetric={pollFetchMetric}
                        fetchMetric={fetchMetric}
                        dashboard={dashboard}
                    />
                </>
            );
        },
        [
            state,
            planId,
            clientId,
            dashboard,
            fetchMetric,
            onDeleteChart,
            onEditChart,
            pollFetchMetric,
            graphsWithExistingBeat,
            toggleExistingBeat,
            t
        ]
    );
    return (
        <ReportGrid
            customRowHeight={250}
            customLayoutOption={layoutOptions}
            items={items}
            savedLayout={props.savedLayout}
            onLayoutChange={props.onLayoutChange}
            currentBreakpoint={state.currentBreakpoint}
            currentCols={state.currentCols}
            onBreakpointChange={handleBreakpointChange}
            renderItem={renderItem}
        />
    );
};
export default PjpGraphsGrid;
